import api from './api';
import { extractAxiosErrorMessage } from './errorUtils';
import type { BillingPeriod } from './paymentTiers';

// =============================================================================
// Existing endpoints — DO NOT TOUCH (used by the viewer-→-creator upgrade flow)
// =============================================================================

export interface UpgradeSessionResponse {
  sessionId: string;
  url: string;
}

export interface VerifyUpgradeResponse {
  success: boolean;
  userId?: string;
  newTier?: string;
  aliId?: string;
  message?: string;
}


export async function createUpgradeSession(
  tier: string,
  initials: string,
  billingPeriod: 'monthly' | 'yearly' = 'monthly'
): Promise<UpgradeSessionResponse> {
  try {
    const response = await api.post('/api/subscription/upgrade', { tier, initials, billingPeriod });
    return response.data as UpgradeSessionResponse;
  } catch (err) {
    throw new Error(extractAxiosErrorMessage(err, 'Failed to create upgrade session'));
  }
}

export async function verifyUpgrade(
  sessionId: string
): Promise<VerifyUpgradeResponse> {
  try {
    const response = await api.post('/api/subscription/upgrade/verify', { sessionId });
    return response.data as VerifyUpgradeResponse;
  } catch (err) {
    throw new Error(extractAxiosErrorMessage(err, 'Failed to verify upgrade'));
  }
}

export async function getPortalUrl(): Promise<string> {
  try {
    const response = await api.get('/api/subscription/portal');
    return (response.data as { url: string }).url;
  } catch (err) {
    throw new Error(extractAxiosErrorMessage(err, 'Failed to open billing portal'));
  }
}

// =============================================================================
// Subscription management page — per-catalog subscriptions.
//
// Each catalog (primary + add-ons) has its own Stripe Subscription with its
// own billing cycle, payment method, cancel flag and discount. Wire shape
// mirrors the Pydantic SubscriptionState model on the server.
// =============================================================================

// 'expired' (GH#1254): the catalog's subscription actually ended — the row
// state comes from user_catalogs, not Stripe. Reactivation is a NEW checkout.
export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'trialing' | 'expired';

export type CatalogRelationship =
  | 'Estate'
  | 'Family'
  | 'Collaborator'
  | 'Represented by'
  | 'Other';

export interface DiscountCode {
  code: string;
  percentOff: number;
  amountOffCents?: number;
  expiresAt: string;
}

export interface PaymentMethod {
  brand: string;
  last4: string;
}

/**
 * One catalog (primary or add-on) with its full Stripe state.
 *
 * The server backs each row with its own Stripe Subscription, so every
 * field except `catalogAliId` / `name` / `creatorName` / `relationship` /
 * `isPrimary` can vary per catalog. Primary catalog has `relationship`
 * set to `null` and `isPrimary` true.
 */
export interface Catalog {
  catalogAliId: string;
  name: string;
  creatorName: string;
  relationship: CatalogRelationship | null;
  isPrimary: boolean;
  status: SubscriptionStatus;
  billingPeriod: BillingPeriod;
  renewDate: string;
  cancelAtPeriodEnd: boolean;
  priceCents: number;
  stripeSubscriptionId: string | null;
  discountCode: DiscountCode | null;
  paymentMethod: PaymentMethod | null;
}

export interface SubscriptionState {
  tier: 'creator' | 'commercial';
  catalogs: Catalog[];
}

export interface BillingPeriodChangePreview {
  proratedChargeCents: number;
  nextRenewDate: string;
  currentBillingPeriod: BillingPeriod;
}

export interface CatalogCheckoutResponse {
  sessionId: string;
  url: string;
}

// Soft-cap from the design ("Up to 5 per account"). Mirrors the server-side
// constant ``ADDON_CATALOGS_SOFT_CAP`` in subscription_service.py. The UI
// uses this for the "Contact us" affordance; the server enforces it
// authoritatively with a 409 response.
export const ADDON_CATALOGS_SOFT_CAP = 5;

export interface CreateCatalogInput {
  creatorName: string;
  relationship: CatalogRelationship;
  billingPeriod: BillingPeriod;
  /** User-confirmed 2-letter ALI ID prefix for the new catalog (LL-NXX). */
  initials: string;
}

export async function getSubscription(): Promise<SubscriptionState> {
  try {
    const response = await api.get('/api/subscription');
    return response.data as SubscriptionState;
  } catch (err) {
    throw new Error(extractAxiosErrorMessage(err, 'Failed to load subscription'));
  }
}

function catalogPath(catalogAliId: string, suffix = ''): string {
  return `/api/subscription/catalogs/${encodeURIComponent(catalogAliId)}${suffix}`;
}

export async function previewBillingPeriodChange(
  catalogAliId: string,
  target: BillingPeriod
): Promise<BillingPeriodChangePreview> {
  try {
    const response = await api.post(catalogPath(catalogAliId, '/change-billing-period/preview'), { period: target });
    return response.data as BillingPeriodChangePreview;
  } catch (err) {
    throw new Error(extractAxiosErrorMessage(err, 'Failed to preview billing change'));
  }
}

export async function changeBillingPeriod(
  catalogAliId: string,
  target: BillingPeriod
): Promise<SubscriptionState> {
  try {
    const response = await api.post(catalogPath(catalogAliId, '/change-billing-period'), { period: target });
    return response.data as SubscriptionState;
  } catch (err) {
    throw new Error(extractAxiosErrorMessage(err, 'Failed to change billing period'));
  }
}

export async function cancelSubscription(
  catalogAliId: string,
  reason?: string,
  keepPublicVisible?: boolean
): Promise<SubscriptionState> {
  try {
    const response = await api.post(catalogPath(catalogAliId, '/cancel'), {
      reason: reason ?? null,
      keepPublicVisible: keepPublicVisible ?? null,
    });
    return response.data as SubscriptionState;
  } catch (err) {
    throw new Error(extractAxiosErrorMessage(err, 'Failed to cancel subscription'));
  }
}

/**
 * Start a reactivation Checkout for an EXPIRED catalog (GH#1254). Distinct
 * from :func:`reactivateSubscription` (which un-cancels a still-live
 * subscription): the expired catalog's subscription is gone, so this is a
 * brand-new checkout. On return, the page sees
 * ``?catalog_reactivated=true&session_id=…`` and calls
 * :func:`verifyCatalogCheckout` to finalize (same idempotent endpoint).
 */
export async function createReactivateCheckout(
  catalogAliId: string,
  billingPeriod: BillingPeriod
): Promise<CatalogCheckoutResponse> {
  try {
    const response = await api.post(catalogPath(catalogAliId, '/reactivate-checkout'), {
      billingPeriod,
    });
    return response.data as CatalogCheckoutResponse;
  } catch (err) {
    throw new Error(extractAxiosErrorMessage(err, 'Failed to start reactivation checkout'));
  }
}

export async function reactivateSubscription(catalogAliId: string): Promise<SubscriptionState> {
  try {
    const response = await api.post(catalogPath(catalogAliId, '/uncancel'));
    return response.data as SubscriptionState;
  } catch (err) {
    throw new Error(extractAxiosErrorMessage(err, 'Failed to reactivate subscription'));
  }
}

/**
 * Start an add-catalog Checkout Session. Returns the URL the frontend
 * should redirect to. On return, the page sees ``?catalog_success=true&session_id=…``
 * and calls :func:`verifyCatalogCheckout` to finalize.
 */
export async function createCatalogCheckout(
  input: CreateCatalogInput
): Promise<CatalogCheckoutResponse> {
  try {
    const response = await api.post('/api/subscription/catalogs/checkout', {
      creatorName: input.creatorName,
      relationship: input.relationship,
      billingPeriod: input.billingPeriod,
      initials: input.initials,
    });
    return response.data as CatalogCheckoutResponse;
  } catch (err) {
    throw new Error(extractAxiosErrorMessage(err, 'Failed to start checkout'));
  }
}

/**
 * Finalize an add-catalog Checkout after Stripe redirects back. Idempotent
 * — safe to call repeatedly with the same session id (success_url + webhook
 * race, or user-driven page refresh).
 */
export async function verifyCatalogCheckout(sessionId: string): Promise<SubscriptionState> {
  try {
    const response = await api.post('/api/subscription/catalogs/checkout/verify', { sessionId });
    return response.data as SubscriptionState;
  } catch (err) {
    throw new Error(extractAxiosErrorMessage(err, 'Failed to verify checkout'));
  }
}

export async function removeCatalog(catalogAliId: string): Promise<SubscriptionState> {
  try {
    const response = await api.delete(`/api/subscription/catalogs/${encodeURIComponent(catalogAliId)}`);
    return response.data as SubscriptionState;
  } catch (err) {
    throw new Error(extractAxiosErrorMessage(err, 'Failed to remove catalog'));
  }
}

export async function applyDiscountCode(
  catalogAliId: string,
  code: string
): Promise<SubscriptionState> {
  try {
    const response = await api.post(catalogPath(catalogAliId, '/discount'), { code });
    return response.data as SubscriptionState;
  } catch (err) {
    throw new Error(extractAxiosErrorMessage(err, 'Failed to apply discount code'));
  }
}

export async function removeDiscountCode(catalogAliId: string): Promise<SubscriptionState> {
  try {
    const response = await api.delete(catalogPath(catalogAliId, '/discount'));
    return response.data as SubscriptionState;
  } catch (err) {
    throw new Error(extractAxiosErrorMessage(err, 'Failed to remove discount code'));
  }
}
